<template>
  <div class="text-sm">
    <div class="flex items-center justify-between mb-3">
      <span class="uppercase track-tight font-bold">{{$t('cart.subtotal')}}</span>
      <span class="font-sans-serif font-bold" v-if="!isValidCoupon">
        {{ formatter.format(subTotalToPay / 100) }}
      </span>
      <span class="text-b2c-red-300 line-through" v-else>
        <span class="font-sans-serif font-bold">
          {{ formatter.format(subTotalWithTaxes / 100) }}
        </span>
      </span>
    </div>
    <div class="flex items-center justify-between mt-2" v-if="isValidCoupon">
        <div class="flex flex-col">
          <span class="uppercase track-tight font-bold">{{$t('checkout.discounted-subtotal')}}</span>
        </div>
        <span class="font-sans-serif font-bold">
          {{ formatter.format(subTotalToPay / 100) }}
        </span>
      </div>
    <h4 class="mb-1 text-gray-800 font-light" v-if="!options.shipping">
      {{$t('cart.shippingCalculate')}}
    </h4>
    <div v-else>
      <div class="flex flex-col my-6">
        <div class="flex items-center justify-between">
          <span class="uppercase track-tight font-bold">{{$t('cart.shipping')}}</span>
          <span class="font-sans-serif font-bold">
            {{ freeShipping ? 'Free Shipping' : formatter.format(shippingToPay / 100) }}
          </span>
        </div>
        <span :class="fontThin()" v-if="!freeShipping">
          {{ shippingMessage }}
        </span>
      </div>
      <div class="flex justify-between font-bold" v-if="showCashMessage">
        <span class="uppercase">
          {{ $t('checkout.commission') }}
        </span>
        <span>
          {{ formatter.format(paymentMethod.fee/100) }}
        </span>
      </div>
      <div class="flex flex-col justify-between">
        <span class="uppercase w-full font-bold pb-2">
          {{ $t('checkout.coupon') }}
        </span>
        <div v-if="!showCoupons">
          <button @click="showCoupons = true">
            {{ $t('coupons.hasACoupon') }}
          </button>
        </div>
        <section v-else>
          <div v-if="selectedCoupon === 'none'" class="pb-2">
            {{ $t('coupons.writeCoupon') }}
            <div class="flex w-full items-center justify-between" v-if="!isValidCoupon">
              <div class="mr-4">
                <input type="text" v-model="couponCode" class="border-b border-gray-400">
              </div>
              <button @click="applyCoupon"
                class="transform hover:scale-105 bg-b2c-red-300 text-white px-4 py-1 rounded">
                {{ $t('coupons.apply') }}
              </button>
            </div>
            <div class="flex w-full items-center justify-between pt-2" v-else>
              <div class="border-b border-gray-400 w-1/2 flex justify-between items-center">
                {{ couponCode }}
                <button class="hover:text-b2c-red-300" @click="removeCoupon">
                  <close-icon class="w-3" />
                </button>
              </div>
              <span class="text-green-500">
                {{ $t('coupons.validCode') }}
              </span>
            </div>
          </div>
          <section v-if="customerCoupons.length>0 && !couponCode" >
            <span class="pt-2" v-if="selectedCoupon === 'none'">{{ $t('coupons.chooseCoupon') }}</span>
            <select v-model="selectedCoupon" class="border py-1 mt-3" @change="couponHasBeenSelected">
              <option :value="'none'">{{ $t('coupons.noSelected') }}</option>
              <option
                v-for="coupon in customerCoupons"
                :key="coupon.id"
                :value="coupon"
                class="py-1">
                  {{ coupon.code }} - {{ getCouponValue(coupon) }}
              </option>
            </select>
          </section>
        </section>
      </div>

    </div>
    <div class="flex items-center justify-between mt-6 pt-1 border-t border-blackish">
      <div class="flex flex-col">
        <span class="uppercase track-tight font-bold">{{$t('cart.total')}}</span>
        <span class="track-tight" :class="fontThin()" v-if="isTaxable">{{$t('cart.vat')}}</span>
        <span class="track-tight" :class="fontThin()" v-else>{{$t('cart.noVat')}}</span>
      </div>
      <span class="font-sans-serif font-bold">
        {{ options.shipping ? formatter.format(totalToPay / 100) :  formatter.format(subTotalToPay / 100) }}
      </span>
    </div>
    <span v-if="showDelayMessage" class="text-xs py-2 italic text-justify mt-6">
      {{ $t('checkout.delay-message') }}
    </span>
    <span v-if="showCashMessage" class="text-xs py-2 italic text-justify">
      {{ $t('checkout.cash-message1') }} {{ formatter.format(paymentMethod.fee/100) }} {{ $t('checkout.cash-message2') }}
    </span>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getCustomerPersonalCoupons, getAllCustomerCoupons } from '@/services/OrdersService';
import CloseIcon from '../graphics/CloseIcon.vue';

export default {
  components: { CloseIcon },

  props: ['appOptions', 'shippingMessage', 'paymentMethod'],

  computed: {
    options() {
      return {
        ...this.defaults,
        ...this.appOptions,
      };
    },
    ...mapGetters({
      subTotal: 'cart/subTotal',
      subTotalWithTaxes: 'cart/subTotalWithTaxes',
      subTotalToPay: 'cart/subTotalToPay',
      roundedSubTotalWithTaxes: 'cart/roundedSubTotalWithTaxes',
      totalWithTaxes: 'cart/totalWithTaxes',
      shippingWithTaxes: 'cart/shippingWithTaxes',
      shippingToPay: 'cart/shippingToPay',
      freeShipping: 'cart/freeShipping',
      totalToPay: 'cart/totalToPay',
      totalWithoutCoupon: 'cart/totalWithoutCoupon',
      isTaxable: 'auth/isTaxable',
      isCash: 'cart/isCash',
      user: 'auth/user',
      availableCoupon: 'cart/availableCoupon',
    }),

    showDelayMessage() {
      if (this.paymentMethod && (this.paymentMethod.slug === 'sepa' || this.paymentMethod.slug === 'sofort')) {
        return true;
      }
      return false;
    },

    showCashMessage() {
      if (this.paymentMethod && this.paymentMethod.slug === 'cash') {
        return true;
      }
      return false;
    },
  },

  data() {
    return {
      defaults: {
        shipping: true,
      },
      customerCoupons: [],
      allCoupons: [],
      couponCode: '',
      selectedCoupon: 'none',
      showCoupons: false,
      isValidCoupon: false,
    };
  },

  watch: {
    paymentMethod() {
      if (this.paymentMethod) {
        this.checkCash();
      }
    },
  },

  created() {
    this.$store.dispatch('cart/setAvailableCoupon', null);
    getCustomerPersonalCoupons(this.user.customer.id, true).then(({ data }) => {
      this.customerCoupons = data;
    });
    getAllCustomerCoupons(this.user.customer.id, true).then(({ data }) => {
      this.allCoupons = data;
    });
  },

  mounted() {
    if (this.paymentMethod) {
      this.checkCash();
    }
  },

  methods: {
    checkCash() {
      if ((this.paymentMethod.slug === 'cash' && !this.isCash) || (this.paymentMethod.slug !== 'cash' && this.isCash)) {
        this.$store.dispatch('cart/updateIsCash', this.paymentMethod.fee);
      }
    },

    getCouponValue(coupon) {
      if (coupon.type === 'P') {
        return `${coupon.value}%`;
      }
      return this.formatter.format(coupon.pivot.deducted_value / 100);
    },

    couponHasBeenSelected() {
      this.isValidCoupon = false;
      if (this.selectedCoupon !== 'none') {
        this.isValidCoupon = true;
      }
      this.$store.dispatch('cart/setAvailableCoupon', this.selectedCoupon !== 'none' ? this.selectedCoupon : null);
    },

    applyCoupon() {
      if (!this.checkAvailableCoupon()) {
        this.createNotification('error', this.$t('coupons.invalid'));
      }
    },

    checkAvailableCoupon() {
      this.isValidCoupon = false;
      if (!this.couponCode) {
        return false;
      }
      const availableCoupon = this.allCoupons.find((coupon) => coupon.code === this.couponCode);
      if (availableCoupon) {
        this.isValidCoupon = true;
        this.$store.dispatch('cart/setAvailableCoupon', availableCoupon);
        return true;
      }
      return false;
    },

    removeCoupon() {
      this.isValidCoupon = false;
      this.couponCode = '';
      this.$store.dispatch('cart/setAvailableCoupon', null);
    },
  },

};
</script>
